import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { QbItaStep, getDate } from '@fwmovilidad/qb-home-ita-components-lib';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Flow, RoutePaths } from '@global/routePaths';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { FOOTER_LINKS, ICON_BLOG, ICON_PAYMENT, ICON_USER, VERTI_URL } from '@global/constants';
import { FooterLink } from '@core/models/swagger/footerLink';
import { findParameterResultById, getLastLevelPath } from '@global/utils';
import { map, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { getInitFlow } from '@store/flow-data/init-flow';
import { InitFlow, ProductLine, Quote } from '@core/models/swagger';
import { ExitIntentService } from '@core/services/exit-modal/exit-intent-modal-service';
import {
  SectionIdSummaryLite,
  SectionSummaryLite,
  SubSectionIdSummaryLite,
  SummaryLiteData,
} from '@core/models/swagger/summaryLiteData';
import { getQuote } from '@store/flow-data/quote';
import { getSelectedProductLine } from '@store/flow-data/general';
import { PaymentFrequency } from '@core/models/swagger/bff';
import { Router } from '@angular/router';

@Component({
  selector: 'qb-de-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  footerYear = getDate().getFullYear();
  FOOTER_LINKS: readonly FooterLink[] = FOOTER_LINKS;
  footerLinks: FooterLink[];
  VERTI_URL: string = VERTI_URL;

  @Input() contentCentered = false;
  @Input() contentPadding = false;

  // Page spinner
  @Input() lockUserInteraction: boolean;

  // Content component info
  @Input() stepPageTitle?: string;
  @Input() stepPageDescription?: string;
  @Input() stepPageIcon?: string;

  // Summary lite
  @Input() showSummaryLite = false;
  @Input() showSummaryLiteCoverages: boolean = false;
  @Input() showSummaryLitePDF: boolean = false;
  @Input() summaryLiteSectionExpandedId: SectionIdSummaryLite;
  @Output() summaryLitePDFClick: EventEmitter<string> = new EventEmitter();

  // Header inputs
  @Input() headerPromotionalImage?: string;
  @Input() headerPromotionalUrl?: string;
  @Input() headerPromotionalShowInd = true;
  @Input() hideHeaderTitleInd?: boolean;

  // Footer inputs
  @Input() footerCookiesUrl?: string;
  @Input() footerPrivacyUrl?: string;
  @Input() footerDocumentUrl?: string;
  // Steps inputs
  @Input() steps: QbItaStep[];
  @Input() showSteps = true;
  /**
   * Decimal format. First number is the main step, second number is substep
   * @example 1.1
   */
  @Input() currentStep: string;
  @Input() percent?: number;
  @Output() stepClicked: EventEmitter<string> = new EventEmitter();

  // Back button
  @Input() hideBackButton = false;
  @Input() backBtnTitle?: string;
  @Input() backBtnDescription?: string;
  @Input() showNewEstimateButton = false;
  @Output() backBtnClick: EventEmitter<void> = new EventEmitter();

  // Continue button
  @Input() hideNextButton = false;
  @Input() hideNextButtonMobile = false;
  @Input() nextBtnTitle?: string;
  @Input() nextBtnDisabled?: boolean;
  @Input() nextBtnPosition?: string;
  @Input() nextButtonShowSpinner?: boolean;
  @Output() nextBtnClick: EventEmitter<void> = new EventEmitter();

  // Secondary button
  @Input() hideSecondaryButton = true;
  @Input() hideSecondaryButtonMobile = false;
  @Input() secondaryBtnTitle?: string;
  @Input() secondaryBtnDisabled?: boolean;
  @Input() secondaryButtonShowSpinner?: boolean;
  @Output() secondaryBtnClick: EventEmitter<void> = new EventEmitter();

  @Input() flowName: Flow;
  @Input() stepName: string;
  @Input() showSeoFooter: boolean;

  @Input() pageLoading: boolean;

  onDesktop: boolean;

  subtitle: string;

  getLastLevelPath = getLastLevelPath;

  RoutePaths = RoutePaths;

  Flow = Flow;

  initFlowData: InitFlow;

  summaryLiteData$: Observable<SummaryLiteData | undefined>;

  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly EXCLUSION_PATH = '/5-danke';

  PaymentFrequencyTranslation: Record<PaymentFrequency, string> = {
    [PaymentFrequency.Yearly]: 'Jährlich',
    [PaymentFrequency.HalfYearly]: 'Halbjährlich',
    [PaymentFrequency.Quarterly]: 'Vierteljährlich',
    [PaymentFrequency.Monthly]: 'Monatlich',
  };

  constructor(
    private readonly store: Store<AppState>,
    private readonly translateService: TranslateService,
    private readonly exitIntentService: ExitIntentService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.onDesktop = window.innerWidth > 768;

    this.footerLinks = FOOTER_LINKS.map(link => {
      if (link.type === 'copyright') {
        return {
          ...link,
          label: this.translateService.instant('FOOTER.COPYRIGHT.TEXT', { year: getDate().getFullYear() }),
        };
      }
      return link;
    });

    this.store.pipe(select(getInitFlow), takeUntil(this.destroy$)).subscribe((initFlowData: InitFlow) => {
      this.initFlowData = initFlowData;
    });

    if (this.showSummaryLite) {
      this.summaryLiteData$ = combineLatest([
        this.store.pipe(select(getQuote)),
        this.store.pipe(select(getSelectedProductLine)),
      ])
        .pipe(
          map(([quote, productLine]) => {
            return this.mapQuoteToSummaryLiteData(quote, productLine);
          })
        )
        .pipe(takeUntil(this.destroy$));
    }

    if (this.onDesktop && !sessionStorage.getItem('exit-intent-shown')) {
      this.setupExitIntentTriggers();
    }
  }

  replaceHyphensWithSpaces(text: string): string {
    return text.replace(/-/g, ' ');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  goToVerti(): void {
    window.location.href = 'https://www.verti.de/';
  }

  onHeaderPhoneNumberClick(): void {
    const rawVertiPhone = this.initFlowData?.campaignInfo?.telephoneNumber;

    if (!rawVertiPhone) {
      return;
    }

    window.location.href = `tel:${rawVertiPhone.replace(/[^\d+]/g, '')}`;
  }

  private setupExitIntentTriggers(): void {
    this.router.events.subscribe(() => {
      if (window.location.pathname.includes(this.EXCLUSION_PATH)) {
        this.destroyExitIntentTriggers();
      }
    });

    if (window.location.pathname.includes(this.EXCLUSION_PATH)) return;

    const resetTimer = (): void => {
      this.clearCurrentTimer();
      this.inactivityTimer = setTimeout(() => this.triggerExitIntent(), 90000);
    };

    this.addEventListeners(resetTimer);
    resetTimer();
  }

  private readonly mouseLeaveHandler = (e: MouseEvent): void => {
    if (e.clientY <= 0) {
      this.triggerExitIntent();
    }
  };

  private readonly resetTimerHandler = (): void => {
    this.clearCurrentTimer();
    this.inactivityTimer = setTimeout(() => this.triggerExitIntent(), 90000);
  };

  private addEventListeners(handler: () => void): void {
    ['mousemove', 'scroll', 'click'].forEach(event => window.addEventListener(event, this.resetTimerHandler));
    document.addEventListener('mouseleave', this.mouseLeaveHandler);
  }

  private clearCurrentTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  public destroyExitIntentTriggers(): void {
    this.clearCurrentTimer();

    ['mousemove', 'scroll', 'click'].forEach(event => window.removeEventListener(event, this.resetTimerHandler));
    document.removeEventListener('mouseleave', this.mouseLeaveHandler);
  }

  private triggerExitIntent(): void {
    this.exitIntentService.showModal();
  }

  private mapQuoteToSummaryLiteData(quote: Quote, productLine: ProductLine): SummaryLiteData {
    const policyholder = quote?.policyholderData;
    const partner = quote?.partnerData;
    const previousInsurance = quote?.previousInsuranceData;
    const payment = quote?.bankAccountOwnerData;

    const currentStepToNumber = Number(this.currentStep);

    const getConditionalProperty = (key: string, value: string | undefined | null) =>
      value !== null && value !== undefined && value !== '' ? { [key]: value } : {};

    const policyholderSubSection = {
      id: SubSectionIdSummaryLite.POLICYHOLDER,
      title: this.translateService.instant('SUMMARY_LITE.SECTION.POLICYHOLDER.SUBSECTION.POLICYHOLDER.TITLE'),
      ...getConditionalProperty(
        'name',
        policyholder?.name && policyholder?.surname ? `${policyholder?.name} ${policyholder?.surname}` : '-'
      ),
      ...getConditionalProperty('phone', policyholder?.contact?.phone ? `${policyholder?.contact?.phone}` : '-'),
      ...getConditionalProperty('email', policyholder?.contact?.email ? `${policyholder?.contact?.email}` : '-'),
      ...getConditionalProperty(
        'address',
        policyholder?.address?.street && policyholder?.address?.houseNumber
          ? `${policyholder?.address?.street} ${policyholder?.address?.houseNumber}`
          : '-'
      ),
      ...getConditionalProperty(
        'city',
        policyholder?.address?.postCode && policyholder?.address?.city
          ? `${policyholder?.address?.postCode} ${policyholder?.address?.city}`
          : '-'
      ),
    };

    const partnerSubSection =
      currentStepToNumber >= 3.5
        ? {
            id: SubSectionIdSummaryLite.FAMILY,
            title: this.translateService.instant('SUMMARY_LITE.SECTION.POLICYHOLDER.SUBSECTION.FAMILY.TITLE'),
            ...getConditionalProperty(
              'marital_Status',
              policyholder?.maritalStatus ? `${policyholder?.maritalStatus.parameterName}` : '-'
            ),
            // ...(policyholder.maritalStatus?.parameterId === 'Married'
            //   ? {
            //       ...getConditionalProperty(
            //         'name',
            //         partner?.name && partner?.surname ? `${partner?.name} ${partner?.surname}` : '-'
            //       ),
            //     }
            //   : {}),
          }
        : null;

    const prevInsuranceSubSection = {
      id: SubSectionIdSummaryLite.PREVIOUS_INSURANCE,
      ...getConditionalProperty(
        'insurer',
        previousInsurance?.previousInsurance === false
          ? this.translateService.instant('YES_NO_OPTIONS.KEINE')
          : previousInsurance?.insurer
            ? `${previousInsurance?.insurer.parameterName}`
            : '-'
      ),
      ...(previousInsurance?.previousInsurance
        ? {
            ...getConditionalProperty(
              'canceller',
              previousInsurance?.canceller ? `${previousInsurance?.canceller.parameterName}` : '-'
            ),
            ...getConditionalProperty(
              'claims',
              previousInsurance?.claims ? `${previousInsurance?.claims.parameterName}` : '-'
            ),
          }
        : {}),
    };
    const paymentSubSection = {
      id: SubSectionIdSummaryLite.PAYMENT,
      ...getConditionalProperty('iban_number', payment?.iban ? `${payment?.iban}` : '-'),
      ...getConditionalProperty(
        'account_owner',
        !payment?.isPerson
          ? payment?.name
            ? `${payment?.name}`
            : '-'
          : payment?.name && payment?.surname
            ? `${payment?.name} ${payment?.surname}`
            : '-'
      ),
    };

    const summarySections: SectionSummaryLite<unknown>[] = [
      {
        id: 'POLICYHOLDER',
        title: this.translateService.instant('SUMMARY_LITE.SECTION.POLICYHOLDER.TITLE'),
        icon: ICON_USER,
        isExpanded: currentStepToNumber <= 3.5,
        subSections: [policyholderSubSection, partnerSubSection].filter(Boolean),
      },
    ];

    if (currentStepToNumber >= 3.6) {
      summarySections.push({
        id: 'PREV_INSURANCE',
        title: this.translateService.instant('SUMMARY_LITE.SECTION.PREV_INSURANCE.TITLE'),
        icon: ICON_BLOG,
        isExpanded: currentStepToNumber === 3.6,
        subSections: [prevInsuranceSubSection],
      });
    }

    if (currentStepToNumber >= 3.7) {
      summarySections.push({
        id: 'PAYMENT',
        title: this.translateService.instant('SUMMARY_LITE.SECTION.PAYMENT.TITLE'),
        icon: ICON_PAYMENT,
        isExpanded: currentStepToNumber === 3.7 || currentStepToNumber === 3.8,
        subSections: [paymentSubSection],
      });
    }

    return {
      title: productLine?.productLine?.value,
      priceDetail: {
        price: productLine?.price?.default,
        frequency: this.PaymentFrequencyTranslation[productLine?.price?.frequency],
        currency: '€',
      },
      deducible:
        this.translateService.instant('SUMMARY_LITE.DEDUCTIBLE') + quote.insuranceData?.deductible.parameterName,
      summarySections: summarySections,
    };
  }
}
